import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NotificationService } from "./notification.service";
import { UserService } from "../services/user/user.service";
import { environment } from "../../environments/environment";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { GenericDialogComponent } from "../shared/generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { Router } from "@angular/router";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { finalize } from "rxjs";
import { Mail, NotificationMessage } from "@models/notification.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { TranslateService } from "@ngx-translate/core";
import { Location } from "@angular/common";

interface Notification {
    id: number;
    userId: number;
    emailBody: string;
    message: string;
    type: string;
    isMessageViewed: boolean;
    mailStatus: string;
    createdAt: string;
    updatedAt: string;
    isDeleted?: boolean;
}

@Component({
    selector: "app-user-notification",
    templateUrl: "./user-notification.component.html",
    styleUrls: ["./user-notification.component.scss"],
    standalone: false,
})
export class UserNotificationComponent implements OnInit {
    public notifications: Notification[] = [];
    public pageIndex = environment.dynamicPagination.pageIndex;
    public pageSize = environment.dynamicPagination.pageSize;
    public userId!: number;
    public totalNotifications = 0;
    public selectedNotifications: Set<number> = new Set<number>();
    public loading: boolean = true;
    public unreadNotificationCount: number = 0;
    public mail = Mail;
    public unreadDeletedCountNotification: number = 0;
    public skipNotification: number = 0;

    constructor(
        private notificationService: NotificationService,
        private userService: UserService,
        private dialog: MatDialog,
        private sanitizer: DomSanitizer,
        private router: Router,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private location: Location,
    ) {}

    ngOnInit(): void {
        this.userService.getUserDetails().subscribe((response) => {
            if (response) {
                this.userId = response.id;
                this.loadNotifications(this.userId, this.skipNotification);
            }
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    public loadNotifications(userId: number, skipNotifications: number): void {
        this.loading = true;
        this.notificationService
            .getNotifications(this.pageSize, userId, skipNotifications)
            .pipe(
                finalize(() => {
                    this.loading = false; // Will run whether it's an error or success
                }),
            )
            .subscribe({
                next: (response) => {
                    this.notifications = [...this.notifications, ...response.rows] as Notification[]; // Append new notifications
                    this.updateTotalNotifications(response.count); // Update the total count of notifications
                    this.unreadNotificationCount = response.totalUnreadCount; // total unread count
                },
                error: (error) => {
                    throw error; // Handle the error
                },
            });
    }

    private updateTotalNotifications(total: number): void {
        this.totalNotifications = total; // Store the total notifications count
    }

    public markNotificationAsViewed(notificationId: number): void {
        this.notificationService.markAsViewednotification(notificationId).subscribe(() => {
            const notification = this.notifications.find((notification) => notification.id === notificationId);
            if (notification && notification.isMessageViewed != true) {
                notification.isMessageViewed = true;

                // Decrement the unread notification count
                this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);
            }
        });
    }

    public openMailPopup(notification: Notification): void {
        const emailBody = JSON.parse(notification.emailBody);
        const safeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(emailBody);

        this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.EmailMessage,
                title: this.getTranslation("genericDialogBox.notification.title"),
                message: safeHtml,
            },
        });
        this.markNotificationAsViewed(notification.id);
    }

    public loadMoreNotifications(): void {
        if (this.showViewMoreButton()) {
            this.pageIndex++;
            this.skipNotification = this.notifications.length;
            this.loadNotifications(this.userId, this.skipNotification);
        }
    }

    public showViewMoreButton(): boolean {
        return this.notifications.length < this.totalNotifications;
    }

    public deleteNotification(id: number): void {
        // Pass a single notification ID as an array for deletion
        this.openDeleteConfirmationDialog([id]);
    }

    public deleteSelectedNotifications(): void {
        // Handle multiple deletions by passing the selected IDs
        const idsToDelete = Array.from(this.selectedNotifications);
        this.openDeleteConfirmationDialog(idsToDelete);
    }

    private confirmDeleteNotifications(ids: number[]): void {
        const matchingNotifications = this.notifications.filter(
            (notification) => ids.includes(notification.id) && !notification.isMessageViewed,
        );

        this.unreadDeletedCountNotification = matchingNotifications.length;

        this.notificationService.deleteNotifications(ids).subscribe({
            next: () => {
                this.unreadNotificationCount -= this.unreadDeletedCountNotification;
                this.unreadDeletedCountNotification = 0;
                this.notifications = this.notifications.filter((notification) => !ids.includes(notification.id));
                this.totalNotifications -= ids.length;
                this.selectedNotifications.clear();

                if (this.notifications.length === 0) {
                    this.skipNotification = this.notifications.length;
                    this.loadNotifications(this.userId, this.skipNotification);
                }

                const message =
                    ids.length > 1
                        ? this.getTranslation("notification.message.selectedNotificationsDeletedsuccessfully")
                        : this.getTranslation("notification.message.Deletedsuccessfully");

                this.snackBar.open(message, SnackBarConfig.Actions.CLOSE, {
                    ...SnackBarConfig.Defaults,
                });
            },
            error: () => {
                this.snackBar.open(NotificationMessage.FailedToDelete, SnackBarConfig.Actions.CLOSE, {
                    ...SnackBarConfig.Defaults,
                });
            },
        });
    }

    private openDeleteConfirmationDialog(ids: number[]): void {
        // Generic dialog for both single and multiple notification deletion
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.RemoveFromNotification,
                title:
                    ids.length > 1
                        ? this.getTranslation("genericDialogBox.deleteNotification.title1")
                        : this.getTranslation("genericDialogBox.deleteNotification.title2"),
                content:
                    ids.length > 1
                        ? this.getTranslation("genericDialogBox.deleteNotification.content")
                        : this.getTranslation("genericDialogBox.deleteNotification.content2"),
                firstBtn: this.getTranslation("genericDialogBox.button.yesBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        // Use a single method to confirm the deletion for both cases
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.confirmDeleteNotifications(ids);
            }
        });
    }

    public toggleSelectNotification(id: number): void {
        if (this.selectedNotifications.has(id)) {
            this.selectedNotifications.delete(id);
        } else {
            this.selectedNotifications.add(id);
        }
    }

    public selectAllNotifications(): void {
        this.notifications.forEach((notification) => {
            this.selectedNotifications.add(notification.id);
        });
    }

    public deselectAllNotifications(): void {
        this.unreadDeletedCountNotification = 0;
        this.selectedNotifications.clear();
    }

    public isAllSelected(): boolean {
        return (
            this.notifications.length > 0 &&
            this.notifications.every((notification) => this.selectedNotifications.has(notification.id))
        );
    }

    public isSomeSelected(): boolean {
        return this.selectedNotifications.size > 0 && !this.isAllSelected();
    }

    public toggleSelectAll(event: MatCheckboxChange): void {
        if (event.checked) {
            this.selectAllNotifications();
        } else {
            this.deselectAllNotifications();
        }
    }

    public onSelectAllButtonClick(): void {
        const event = {
            checked: !this.isAllSelected(),
        } as MatCheckboxChange;

        this.toggleSelectAll(event);
    }

    public goToHomePage(): void {
        this.router.navigate(["/home"]);
    }

    public getUnreadNotificationCount(): number {
        return this.unreadNotificationCount;
    }

    public goBack(): void {
        this.location.back();
    }
}
